<template>
  <section>
    <div v-if="wellData" class="container">
      <div class="grid align-items-center my-3">
        <div class="col">
          <p class="heading-2">
            <icon :icon="['fas', 'oil-well']" size="sm" />
            {{ wellData.wells[0].well_name }}
          </p>

          <p v-if="wellData.wells[0].well_uwi" class="heading-3 text-gray-800">
            <i class="pi pi-map-marker"></i> {{ wellData.wells[0].well_uwi }}
          </p>
        </div>
        <div class="col-fixed">
          <Button
            class="p-button-success"
            @click="
              $router.push({
                name: 'ScenarioInfo',
                params: { sizingId: 'new', scenarioId: 'new' }
              })
            "
          >
            <icon icon="plus-circle" class="mr-1" />
            Create Sizing
          </Button>
        </div>
      </div>
      <SizingsAdvancedSearch v-model:sizings-search="sizingsSearch" hide-well />
      <SizingsTable
        v-model:limit="sizingsLimit"
        v-model:offset="sizingsOffset"
        v-model:sort="sizingMultiSortMeta"
        :sizings="sizings"
        :sizings-aggregate-count="sizingsAggregateCount"
        :loading="sizingsFetching"
        :render-links="true"
        class="p-datatable-sm"
        hide-well
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import type {
  SizingsWithScenarioIdsQueryVariables,
  Sizings_Order_By
} from "gql/api";

import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import {
  Order_By,
  useSizingsWithScenarioIdsQuery,
  useWellsQuery
} from "gql/api";

import SizingsTable from "@/components/sizings/SizingsTable.vue";
import SizingsAdvancedSearch from "@/components/sizings/SizingsAdvancedSearch.vue";
import useUserInfo from "@/composables/auth/useUserInfo";
import {
  sizingsByCustomerERPId,
  sizingsByWellId
} from "@/models/sizings/predicates";
import { useSizingsAdvancedSearch } from "@/composables/advancedSearch/sizings";
import {
  getPrimevueSortMeta,
  getUrqlOrderByMeta,
  type SortMeta
} from "@/composables/primevue/tables";
const props = defineProps<{
  wellId: string;
}>();
const router = useRouter();
const user = useUserInfo();

const wellId = Number(props.wellId);

const selectedWellId = ref<number>();

const { data: wellData } = useWellsQuery({
  variables: computed(() => ({
    withAggregate: true,
    withSizings: true,
    where: { id: { _eq: wellId } }
  }))
});

const sizingsLimit = ref(20);
const sizingsOffset = ref(0);

const sizingMultiSortMeta = ref<SortMeta[]>([
  getPrimevueSortMeta({ journal_updated_at: Order_By.Desc })
]);

const sizingsOrderBy = computed<Sizings_Order_By[]>(() =>
  getUrqlOrderByMeta(sizingMultiSortMeta.value)
);

const { sizingsWhere, sizingsSearch } = useSizingsAdvancedSearch({
  sizingsWhere: {
    ...sizingsByWellId(wellId),
    ...sizingsByCustomerERPId(
      user.is_customer as boolean,
      user.id as string,
      user.customers as string[]
    )
  }
});

const sizingsVariables = computed<SizingsWithScenarioIdsQueryVariables>(() => {
  return {
    withAggregate: true,
    withImportUser: true,
    limit: sizingsLimit.value,
    offset: sizingsOffset.value,
    order_by: sizingsOrderBy.value,
    where: sizingsWhere.value
  };
});

const { fetching: sizingsFetching, ...sizingsWithScenarioIdsQuery } =
  useSizingsWithScenarioIdsQuery({
    variables: sizingsVariables
  });

const sizings = computed(
  () => sizingsWithScenarioIdsQuery.data.value?.sizings ?? []
);
const sizingsAggregateCount = computed(
  () =>
    sizingsWithScenarioIdsQuery.data.value?.sizings_aggregate?.aggregate
      ?.count ?? 0
);

watch(selectedWellId, newWellId => {
  if (newWellId) {
    router.push({
      params: { well: newWellId }
    });
    selectedWellId.value = undefined;
  }
});
</script>
